import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bonus } from 'src/types/bonus';

import { Feedback } from '@bonusly/common-feedback';
import { Box, Button, HStack, Spinner, Stack, Text, VStack } from '@bonusly/design-system';

import { getMonthYear } from '../../../../utils/date';
/* eslint-disable local-rules/no-cross-module-imports */
import { useInfiniteScroll } from '../../../top-navbar/hooks/useInfiniteScroll';
/* eslint-enable local-rules/no-cross-module-imports */

import { useRecentActivity } from '../../hooks/use-recent-activity';
import { BonusCardV2, FeedbackCardV2 } from './index';

type AllActivityViewProps = {
	targetUserId: string;
	viewerId: string;
	/** Optional close handler - when provided, shows "Go Back" button in error state */
	onClose?: () => void;
	// Link to entity functionality (goals, topics, etc.)
	showLinkButtons?: boolean;
	isLinked?: (activityType: 'bonus' | 'feedback', activityId: string) => boolean;
	isLinkLoading?: (activityId: string) => boolean;
	onLink?: (activityType: 'bonus' | 'feedback', activityId: string) => void;
	onUnlink?: (activityType: 'bonus' | 'feedback', activityId: string) => void;
};

type ActivityType = 'received' | 'given';

const filterButtonStyles = (buttonFilter: ActivityType, currentFilter: ActivityType) => {
	const isActive = currentFilter === buttonFilter;
	return {
		borderColor: isActive ? 'transparent' : 'border.default',
		borderWidth: '1px',
		borderRadius: 'xl',
		bgColor: isActive ? 'secondary.bg' : 'transparent',
		color: isActive ? 'secondary.fgOnSurface' : 'text.default',
		_hover: {
			bg: isActive ? 'secondary.bg' : 'secondary.bgAlpha',
		},
	};
};

export const AllActivityView: FC<AllActivityViewProps> = ({
	targetUserId,
	viewerId,
	onClose,
	showLinkButtons = false,
	isLinked,
	isLinkLoading = false,
	onLink,
	onUnlink,
}) => {
	const { t } = useTranslation(['one_on_ones', 'common']);
	const [activityType, setActivityType] = useState<ActivityType>('received');

	// Use recent activity hook with infinite query enabled
	const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } = useRecentActivity({
		targetUserId,
		viewerId,
		activityType,
	});
	// Setup infinite scroll
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const { loadMoreRef } = useInfiniteScroll({
		hasNextPage,
		isLoading,
		onLoadMore: fetchNextPage,
		threshold: 0.05,
		rootMargin: '100px',
	});

	// Flatten all items from all pages into a single array for easier month grouping
	const allItems = data?.pages ? data.pages.flatMap((page) => page.items) : [];

	if (isError) {
		return (
			<Box textAlign="center" p={6}>
				<Text fontSize="md" mb={2} color="text.error">
					Couldn't load activity
				</Text>
				<Text fontSize="sm" mb={4} color="text.softest">
					We're having trouble loading recent activity. This might be a temporary issue.
				</Text>
				{onClose && (
					<Button onClick={onClose} mt={2} variant="alpha" colorScheme="secondary">
						Go Back
					</Button>
				)}
			</Box>
		);
	}

	return (
		<VStack
			data-component="all-activity-view"
			spacing={4}
			align="stretch"
			width="full"
			p={0}
			ref={scrollContainerRef}
			flex={1}
			overflowY="auto"
			height="100%"
			maxHeight="100%"
			sx={{
				'&::-webkit-scrollbar': {
					display: 'none',
				},
				scrollbarWidth: 'none',
			}}
		>
			<HStack spacing={2} justifyContent="flex-start" position="sticky" top={0} bg="bg.canvas" zIndex={1} pb={2}>
				<Button
					onClick={() => setActivityType('received')}
					colorScheme="secondary"
					size="sm"
					py="6px"
					px="10px"
					{...filterButtonStyles('received', activityType)}
				>
					Received
				</Button>
				<Button onClick={() => setActivityType('given')} colorScheme="secondary" size="sm" py="6px" px="10px" {...filterButtonStyles('given', activityType)}>
					Given
				</Button>
			</HStack>

			{isLoading && allItems.length === 0 ? (
				<Box textAlign="center" py={8}>
					<Spinner size="md" color="brand.primary" thickness="3px" speed="0.65s" />
				</Box>
			) : allItems.length === 0 ? (
				<Text color="text.softest" px={4}>
					{t('meeting.carousel.empty_state')}
				</Text>
			) : (
				<Stack gap={3}>
					{/* Render items with month separators */}
					{allItems.map((item, globalIndex) => {
						const prevItem = allItems[globalIndex - 1];
						const currentMonth = getMonthYear(item.createdAt);
						const prevMonth = prevItem ? getMonthYear(prevItem.createdAt) : null;

						// Infinite scroll trigger logic (7 from end)
						const totalItemsLoaded = allItems.length;
						const triggerPosition = Math.max(3, totalItemsLoaded - 7);

						return (
							<Box key={`${item.type}-${item.id}-${globalIndex}`}>
								{/* Month separator: only show if first item or month changes */}
								{(globalIndex === 0 || currentMonth !== prevMonth) && (
									<Text fontWeight="medium" color="text.softest" fontSize={50} mb={2} mt={globalIndex === 0 ? 0 : 2}>
										{currentMonth}
									</Text>
								)}
								{/* Render the actual item */}
								{item.type === 'bonus' ? (
									<BonusCardV2
										bonus={item.data as Bonus}
										variant="default"
										border="1px solid"
										borderColor="border.default"
										marginX={{ base: 'auto', md: 0 }}
										bg="bg.surface"
										width="100%"
										showLinkButton={showLinkButtons}
										isLinked={isLinked?.('bonus', item.id) ?? false}
										isLinkLoading={typeof isLinkLoading === 'function' ? isLinkLoading(item.id) : false}
										onLink={(bonusId: string) => onLink?.('bonus', bonusId)}
										onUnlink={(bonusId: string) => onUnlink?.('bonus', bonusId)}
									/>
								) : item.type === 'feedback' ? (
									<FeedbackCardV2
										feedback={item.data as Feedback}
										isLinked={isLinked?.('feedback', item.id) ?? false}
										isLinkLoading={typeof isLinkLoading === 'function' ? isLinkLoading(item.id) : false}
										onLink={(feedbackId: string) => onLink?.('feedback', feedbackId)}
										onUnlink={(feedbackId: string) => onUnlink?.('feedback', feedbackId)}
										showLinkButton={showLinkButtons}
										variant="flat"
										width="100%"
									/>
								) : null}
								{/* Place invisible trigger 7 items from the end if there are more pages */}
								{globalIndex === triggerPosition - 1 && hasNextPage && <Box ref={loadMoreRef} height="1px" width="100%" visibility="hidden" />}
							</Box>
						);
					})}

					<Box textAlign="center" p={4}>
						{isFetchingNextPage && <Spinner size="md" color="brand.primary" thickness="3px" speed="0.65s" />}
						{!isFetchingNextPage && !hasNextPage && allItems.length > 0 && (
							<Text color="text.softest" fontSize="sm">
								No more activity to load
							</Text>
						)}
					</Box>
				</Stack>
			)}
		</VStack>
	);
};
